import type { Descendant } from 'slate';
import { rteDeserializeHTML } from '@wistia/vhs';
import type { Nilable } from './type-guards';
import { isEmptyString, isNil } from './type-guards';

export const toSentenceCase = (title: Nilable<string>, fallbackIfNil = ''): string =>
  isNil(title) ? fallbackIfNil : `${title.charAt(0).toUpperCase()}${title.slice(1).toLowerCase()}`;

export const stringsAreEqual = (
  stringA: Nilable<string>,
  stringB: Nilable<string>,
  caseInsensitive = false,
): boolean => {
  if (isNil(stringA) && isNil(stringB)) {
    return true;
  }

  if (isNil(stringA) || isNil(stringB)) {
    return false;
  }

  if (caseInsensitive) {
    return stringA.toLowerCase() === stringB.toLowerCase();
  }

  return stringA === stringB;
};

export const stringsAreNotEqual = (
  stringA: Nilable<string>,
  stringB: Nilable<string>,
  caseInsensitive = false,
): boolean => !stringsAreEqual(stringA, stringB, caseInsensitive);

export const stringContains = (
  string: Nilable<string>,
  substring: Nilable<string>,
  caseInsensitive = false,
): boolean => {
  if (isNil(string) || isNil(substring)) {
    return false;
  }

  if (caseInsensitive) {
    return string.toLowerCase().includes(substring.toLowerCase());
  }

  return string.includes(substring);
};

export const stringContainsAny = (
  string: Nilable<string>,
  substrings: Nilable<string[]>,
  caseInsensitive = false,
): boolean => {
  if (isNil(string) || isNil(substrings)) {
    return false;
  }

  return substrings.some((substring) => stringContains(string, substring, caseInsensitive));
};

export const stringToRichTextNodes = (string: Nilable<string>): Descendant[] => {
  return isNil(string) || isEmptyString(string.trim())
    ? [
        {
          type: 'paragraph',
          children: [{ text: '' }],
        },
      ]
    : (rteDeserializeHTML(string) as Descendant[]);
};
